import { ApiCategory, ApiCategoryList } from 'idt-ecommerce-api/_api/api-catalog/api-categories/api-categories.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { UrlsWebHelpers } from 'idt-ecommerce-api/util/urls-web.helpers';

import { CategoriesHelpers } from './categories.helpers';
import { Category, CategoryMetadataType, CategoryMetadata } from './categories.models';

/** Clase de utilidad para transformar los objetos que devuelve el api de inditex en los objetos que usaremos en el cms, widgets y render */
export class CategoriesModelsTransformer {

  public static categoriesFromApi(apiCategoryList: ApiCategoryList, context: EcommerceContext, filter: boolean): Category[] {
    let apiCategories = [...apiCategoryList.categories];

    // reorder apiCategories before parse if necessary
    if (context.config.brandSettings.reorderApiCategories) {
      apiCategories = context.config.brandSettings.reorderApiCategories(apiCategories);
    }

    // parse apiCategories
    let categories = apiCategories.map((apiCategory) => {
      const category = this.categoryFromApi(apiCategory, context);
      return category;
    });

    // filter categories
    if (filter) {
      const appId = context.config.appId;
      categories = CategoriesHelpers.filterCategories(categories, appId);
    }

    return categories;
  }

  public static categoryFromApi(apiCategory: ApiCategory, context: EcommerceContext, categoryTreePath: string = ''): Category {
    const id = apiCategory.id.toString();
    const name = apiCategory.name;
    const key = apiCategory.key;
    const nameEn = apiCategory.nameEn;
    const description = apiCategory.description;
    const shortDescription = apiCategory.shortDescription;
    const redirectToCategoryId = apiCategory.viewCategoryId ? apiCategory.viewCategoryId.toString() : undefined;

    const metadata = CategoriesModelsTransformer.parseCategoryMetadata(apiCategory);

    const sectionPath = metadata.isHiddenInSectionPath ? categoryTreePath : this.addCurrentSectionName(categoryTreePath, apiCategory.name);

    // set url
    const urlWeb = UrlsWebHelpers.getCategoryUrl(context, id, sectionPath);
    const urlCms = UrlsWebHelpers.getCategoryCMSUrl(context, id, sectionPath);
    const urlSeo = UrlsWebHelpers.getCategorySeoUrl(context, apiCategory.categoryUrlParam, apiCategory.categoryUrl);

    const attachments = apiCategory.attachments;

    const category: Category = {
      id,
      metadata,
      name,
      nameEn,
      description,
      shortDescription,
      redirectToCategoryId,
      sectionPath,
      subcategories: [],
      urlWeb,
      urlCms,
      attachments,
      urlSeo,
      key
    };

    // process subcategories recursively
    const subcategories = apiCategory.subcategories.map((apiSubCategory) => {
      const subcategory = CategoriesModelsTransformer.categoryFromApi(apiSubCategory, context, category.sectionPath);
      return subcategory;
    });

    category.subcategories = subcategories;

    return category;
  }

  // ---- Private functions ----
  private static parseCategoryMetadata(apiCategory: ApiCategory): CategoryMetadata {
    let parsedType: CategoryMetadataType;
    switch (apiCategory.type) {
      case '4': case '14': case '16': case '20': case '21': case '22': case '23': case '24': case '28': case '44': case '45':
        parsedType = 'grid';
        break;
      case '30':
        parsedType = 'giftcard';
        break;
      case '1': case '17':
        parsedType = 'lookbook';
        break;
      case '2':
        parsedType = 'legacy-web-redirection';
        break;
      default:
        parsedType = 'unknown';
    }

    const metadata: CategoryMetadata = {
      type: parsedType,
      isHiddenAndroid: false,
      isHiddenIOS: false,
      isHiddenWeb: false,
      isHiddenInSectionPath: false,
      isCmsFake: false,
      isEmpty: false,
      isNew: false,
      isLastSizesShowed: false,
      hasLine: false,
      hasLink: false,
      hasSpace: false,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _debug: { _key: apiCategory.key }
    };

    if (isDefined(apiCategory.key)) {
      const key = apiCategory.key.toLowerCase();
      // The on_color format will always be, the name of the tag, and the hexadecimal code (on_colorff0000)
      const matchColor = key.match(/(?:_on_color)([a-f0-9]+)/);
      metadata.isHiddenWeb = key.includes('hidden') || key.includes('_app') || key.includes('_noweb');
      metadata.isHiddenAndroid = key.includes('_noapp');
      metadata.isHiddenIOS = key.includes('_noapp') || key.includes('_noios');
      metadata.isLastSizesShowed = key.includes('_LS');

      metadata.hasLink = key.includes('_link');
      metadata.hasLine = key.includes('_line');
      metadata.hasSpace = key.includes('espace');
      metadata.isEmpty = key.includes('_empty');

      metadata.isNew = key.includes('tagnew');

      if (matchColor && matchColor[1]) {
        metadata.color = `#${matchColor[1]}`;
      }

      metadata.isHiddenInSectionPath = key.includes('_noseo');
      metadata.isCmsFake = key.includes('_cmsfake');
    }

    return metadata;
  }

  private static addCurrentSectionName(categoryTreePath: string, sectionName: string): string {
    let sectionPath = categoryTreePath;

    sectionName = sectionName.toLowerCase();
    sectionName = sectionName.replace(/^\s+|\s+$/g, '');
    sectionName = sectionName.replace(/<[^>]+>/g, '');
    sectionName = sectionName.replace(/\s|\/|\\/g, '-');
    sectionName = encodeURIComponent(sectionName);

    if (sectionPath !== '') {
      sectionPath += `/${sectionName}`;
    } else {
      sectionPath += sectionName;
    }

    return sectionPath;
  }

}
